import { defaultCtxGetter } from '~/hooks/actions/utils/prepare-action-with-context';
import { getSupportedLanguages } from '~/utils/i18n';

export const GENERAL_CONTENT_HOOK_IDENTIFIER = 'general_content';

export const GENERAL_CONTENT_HOOK_RETURN_PATH_KEY =
  'general_content_hook:return_path';

export const GENERAL_CONTENT_HOOK_BLOCK_PATH_KEY =
  'general_content_hook:block_path';

const GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE =
  'general_content_hook_block_path';

const GENERAL_CONTENT_HOOK_BLOCK_REDIRECT_EXCLUDED_PREFIXES = [
  '/api',
  '/.well-known',
];

const GENERAL_CONTENT_HOOK_BLOCK_REDIRECT_EXCLUDED_PATHS = [
  '/logout',
  '/login',
  '/oauth',
  '/lock',
];

export type GeneralContentHookRules = {
  available_as_hook?: boolean;
  block_app_navigation?: boolean;
  block_app_navigation_on_all_screens?: boolean;
  skip_hook_endpoint?: { source?: string } | null;
  skip_hook_storage_key?: string | null;
};

export type PreloadPluginEntry = {
  identifier?: string;
  screen_id?: string;
};

/**
 * Finds the first general content screen hook entry in preload plugins.
 *
 * @param preloadPlugins - Screen preload plugin configuration.
 * @returns The matching preload plugin entry, if any.
 */
export function getGeneralContentHookEntry(
  preloadPlugins: PreloadPluginEntry[] | undefined
): PreloadPluginEntry | undefined {
  return preloadPlugins?.find(
    (plugin) =>
      plugin?.identifier === GENERAL_CONTENT_HOOK_IDENTIFIER &&
      plugin?.screen_id
  );
}

/**
 * Reads a storage key from sessionStorage and localStorage.
 * Supports both dot and colon notation for namespace separators.
 * Supports multiple keys separated by commas, returning true if any key has a non-empty value.
 * @param storageKey - Configured storage key.
 * @returns Whether a non-empty value exists for the key.
 */
export function isGeneralContentHookSkippedByStorageKey(
  storageKey: string | null | undefined
): boolean {
  if (!storageKey) return false;

  const keys = storageKey
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);

  const matched = keys.some((key) => {
    const directValue = defaultCtxGetter(key);
    if (directValue) return true;

    const alternateKey = key.includes('.')
      ? key.replace(/\./g, ':')
      : key.replace(/:/g, '.');

    return Boolean(defaultCtxGetter(alternateKey));
  });

  return matched;
}

/**
 * Determines whether a skip-hook endpoint feed response indicates the hook
 * should be skipped.
 *
 * @param feed - Feed payload returned by the skip-hook endpoint.
 * @returns `true` when the hook should be skipped.
 */
export function shouldSkipGeneralContentHookFromFeed(feed: unknown): boolean {
  if (!feed || typeof feed !== 'object') return false;

  const feedObject = feed as Record<string, unknown>;

  if (feedObject.skip === true || feedObject.ok === true) return true;

  const entries = feedObject.entry;
  if (!Array.isArray(entries)) return false;

  return entries.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;

    const extensions = (entry as Record<string, unknown>).extensions;
    if (!extensions || typeof extensions !== 'object') return false;

    const extensionObject = extensions as Record<string, unknown>;
    return extensionObject.skip === true || extensionObject.ok === true;
  });
}

/**
 * Resolves the redirect path for a general content hook screen.
 *
 * @param hookScreen - Hook screen configuration.
 * @returns The configured pathname, if available.
 */
export function getGeneralContentHookRedirectPath(hookScreen: {
  general?: { pathname?: string };
}): string | undefined {
  const pathname = hookScreen?.general?.pathname;
  if (typeof pathname !== 'string' || pathname.length === 0) return undefined;

  return pathname;
}

export type GeneralContentHookState = {
  isActive: boolean;
  skipByStorageKey: boolean;
  requiresServerCheck: boolean;
  redirectPath?: string;
  hookScreenId?: string;
  blockAppNavigation?: boolean;
};

/**
 * Screen that blocks every route, when the current screen is not that screen
 * and its storage skip key is still empty.
 *
 * @param screen - Current screen being loaded.
 * @param screens - All configured screens, in layout order.
 * @returns Synthetic preload entry for the first matching screen.
 */
function getAllScreensBlockingHookEntry(
  screen: { id?: string },
  screens: Array<{
    id: string;
    rules?: GeneralContentHookRules;
  }>
): PreloadPluginEntry | undefined {
  const blocker = screens.find(
    (item) =>
      item.rules?.available_as_hook &&
      item.rules.block_app_navigation_on_all_screens &&
      !isGeneralContentHookSkippedByStorageKey(item.rules.skip_hook_storage_key)
  );

  if (!blocker || blocker.id === screen.id) return undefined;

  return {
    identifier: GENERAL_CONTENT_HOOK_IDENTIFIER,
    screen_id: blocker.id,
  };
}

/**
 * Resolves the runtime state for a general content screen hook.
 *
 * @param screen - Current screen being loaded.
 * @param screens - All configured screens.
 * @returns Hook state used by client and server hook handlers.
 */
export function getGeneralContentHookState(
  screen: { hooks?: { preload_plugins?: PreloadPluginEntry[] }; id?: string },
  screens: Array<{
    id: string;
    rules?: GeneralContentHookRules;
    general?: { pathname?: string; custom_url?: string };
  }>
): GeneralContentHookState {
  const hookEntry =
    getGeneralContentHookEntry(screen?.hooks?.preload_plugins) ??
    getAllScreensBlockingHookEntry(screen, screens);

  if (!hookEntry?.screen_id) {
    return {
      isActive: false,
      skipByStorageKey: false,
      requiresServerCheck: false,
    };
  }

  const hookScreen = screens.find((item) => item.id === hookEntry.screen_id);
  const hookRules = hookScreen?.rules;

  if (!hookRules?.available_as_hook || !hookScreen) {
    return {
      isActive: false,
      skipByStorageKey: false,
      requiresServerCheck: false,
    };
  }

  const skipByStorageKey = isGeneralContentHookSkippedByStorageKey(
    hookRules.skip_hook_storage_key
  );

  const redirectPath = getGeneralContentHookRedirectPath(hookScreen);
  const requiresServerCheck = Boolean(
    !skipByStorageKey && hookRules.skip_hook_endpoint?.source
  );

  return {
    isActive: true,
    skipByStorageKey,
    requiresServerCheck,
    redirectPath,
    hookScreenId: hookEntry.screen_id,
    blockAppNavigation: Boolean(
      hookRules.block_app_navigation ||
        hookRules.block_app_navigation_on_all_screens
    ),
  };
}

/**
 * Returns preload plugins that should be handled by the server hook flow.
 *
 * @param preloadPlugins - Screen preload plugin configuration.
 * @returns Preload plugins excluding general content screen hooks.
 */
export function getServerPreloadPlugins(
  preloadPlugins: PreloadPluginEntry[] | undefined
): PreloadPluginEntry[] {
  return (
    preloadPlugins?.filter(
      (plugin) => plugin?.identifier !== GENERAL_CONTENT_HOOK_IDENTIFIER
    ) ?? []
  );
}

/**
 * Persists the path to return to after a general content hook screen completes.
 *
 * @param path - Entry screen path before hook redirect.
 */
export function setGeneralContentHookReturnPath(path: string): void {
  if (typeof sessionStorage === 'undefined') return;

  const existingPath = sessionStorage.getItem(
    GENERAL_CONTENT_HOOK_RETURN_PATH_KEY
  );
  if (existingPath) return;

  sessionStorage.setItem(GENERAL_CONTENT_HOOK_RETURN_PATH_KEY, path);
}

/**
 * Persists the hook screen path while app navigation is blocked.
 * The first path wins until the hook is cleared.
 *
 * @param path - Hook screen pathname.
 */
export function setGeneralContentHookBlockPath(path: string): void {
  if (typeof sessionStorage === 'undefined') return;

  const existingPath = sessionStorage.getItem(
    GENERAL_CONTENT_HOOK_BLOCK_PATH_KEY
  );

  if (!existingPath) {
    sessionStorage.setItem(GENERAL_CONTENT_HOOK_BLOCK_PATH_KEY, path);
  }

  const cookiePath = existingPath || path;
  writeGeneralContentHookBlockPathCookie(cookiePath);
}

/**
 * Reads the hook screen path while app navigation is blocked.
 *
 * @returns Stored hook pathname, if the blocking switch was on at redirect.
 */
export function getGeneralContentHookBlockPath(): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  return sessionStorage.getItem(GENERAL_CONTENT_HOOK_BLOCK_PATH_KEY);
}

/**
 * Stores the return path, and the hook path when navigation should be blocked.
 *
 * @param options - Return path, hook path, and whether navigation is blocked.
 * @param options.returnPath - Screen path that triggered the hook.
 * @param options.redirectPath - Hook screen pathname.
 * @param options.blockAppNavigation - Hook screen `block_app_navigation` rule.
 */
export function rememberGeneralContentHookNavigation({
  returnPath,
  redirectPath,
  blockAppNavigation,
}: {
  returnPath: string;
  redirectPath: string;
  blockAppNavigation: boolean;
}): void {
  if (returnPath === redirectPath) return;

  setGeneralContentHookReturnPath(returnPath);

  if (blockAppNavigation) setGeneralContentHookBlockPath(redirectPath);
}

/**
 * Reads the stored return path for a general content hook screen.
 *
 * @returns Stored path, if available.
 */
export function getGeneralContentHookReturnPath(): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  return sessionStorage.getItem(GENERAL_CONTENT_HOOK_RETURN_PATH_KEY);
}

/**
 * Clears the stored return path and block path for a general content hook screen.
 */
export function clearGeneralContentHookReturnPath(): void {
  if (typeof sessionStorage === 'undefined') return;

  sessionStorage.removeItem(GENERAL_CONTENT_HOOK_RETURN_PATH_KEY);
  sessionStorage.removeItem(GENERAL_CONTENT_HOOK_BLOCK_PATH_KEY);
  clearGeneralContentHookBlockPathCookie();
}

/**
 * Writes the block path into a cookie the server can read on the next navigation.
 *
 * @param path - Hook screen pathname.
 */
function writeGeneralContentHookBlockPathCookie(path: string): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE}=${encodeURIComponent(
    path
  )}; Path=/; SameSite=Lax`;
}

/**
 * Removes the block-path cookie.
 */
function clearGeneralContentHookBlockPathCookie(): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Builds a Set-Cookie header value that clears the server-visible block-path cookie.
 *
 * @returns Set-Cookie header value.
 */
export function getClearGeneralContentHookBlockPathCookieHeader(): string {
  return `${GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Normalizes pathnames for exclusion matching.
 * Keeps root path as `/` and trims trailing slashes for all other paths.
 *
 * @param pathname - Request pathname.
 * @returns Normalized pathname.
 */
function normalizeGeneralContentHookPathname(pathname: string): string {
  if (pathname === '/') return pathname;

  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * Whether the pathname should bypass hook-block server redirects.
 *
 * @param pathname - Request pathname.
 * @returns True when this pathname is always allowed.
 */
export function isGeneralContentHookBlockRedirectExcludedPath(
  pathname: string
): boolean {
  const normalizedPathname = normalizeGeneralContentHookPathname(pathname);

  if (
    GENERAL_CONTENT_HOOK_BLOCK_REDIRECT_EXCLUDED_PATHS.includes(
      normalizedPathname
    )
  ) {
    return true;
  }

  return GENERAL_CONTENT_HOOK_BLOCK_REDIRECT_EXCLUDED_PREFIXES.some(
    (prefix) =>
      normalizedPathname === prefix ||
      normalizedPathname.startsWith(`${prefix}/`)
  );
}

/**
 * Reads the blocking hook pathname from the request cookie.
 *
 * @param request - Incoming request.
 * @returns Hook pathname, or null when the cookie is missing or invalid.
 */
function readGeneralContentHookBlockPath(request: Request): string | null {
  const header = request.headers.get('cookie');
  if (!header) return null;

  const entry = header
    .split(';')
    .map((part) => part.trim())
    .find((part) =>
      part.startsWith(`${GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE}=`)
    );
  if (!entry) return null;

  let blockPath = '';
  try {
    blockPath = decodeURIComponent(
      entry.slice(GENERAL_CONTENT_HOOK_BLOCK_PATH_COOKIE.length + 1)
    );
  } catch {
    return null;
  }

  if (!blockPath.startsWith('/') || blockPath.startsWith('//')) return null;

  return blockPath;
}

/**
 * Non-default language prefix on a pathname, such as `/fr` on `/fr/profiles`.
 * The default language is not prefixed; the screen loader strips it.
 *
 * @param pathname - Request or stored pathname.
 * @param supportedLanguages - App languages. The first entry is the default.
 * @returns The prefix, or an empty string when the path is not localized.
 */
function nonDefaultLanguagePrefix(
  pathname: string,
  supportedLanguages: string[]
): string {
  const segment = pathname.split('/')[1];
  const defaultLanguage = supportedLanguages[0];
  if (!segment || !defaultLanguage || segment === defaultLanguage) return '';
  if (!supportedLanguages.includes(segment)) return '';

  return `/${segment}`;
}

/**
 * Pathname with a non-default language prefix removed.
 *
 * @param pathname - Request or stored pathname.
 * @param supportedLanguages - App languages. The first entry is the default.
 * @returns The screen path used by the layout.
 */
function pathnameWithoutLanguagePrefix(
  pathname: string,
  supportedLanguages: string[]
): string {
  const prefix = nonDefaultLanguagePrefix(pathname, supportedLanguages);
  if (!prefix) return pathname;

  return pathname.slice(prefix.length) || '/';
}

/**
 * Resolves where a blocking hook should send this pathname.
 * `/fr/profiles` and `/profiles` are the same screen. A redirect keeps the request language.
 *
 * @param options - Pathname being requested, stored hook path, and app languages.
 * @param options.pathname - Current pathname.
 * @param options.blockPath - Stored hook pathname.
 * @param options.supportedLanguages - App languages. The first entry is the default.
 * @returns Localized hook path, or null when this pathname is already that screen.
 */
export function resolveGeneralContentHookBlockPath({
  pathname,
  blockPath,
  supportedLanguages,
}: {
  pathname: string;
  blockPath: string;
  supportedLanguages: string[];
}): string | null {
  const normalizedCurrentPath = pathnameWithoutLanguagePrefix(
    pathname,
    supportedLanguages
  );
  const normalizedBlockPath = pathnameWithoutLanguagePrefix(
    blockPath,
    supportedLanguages
  );

  if (normalizedCurrentPath === normalizedBlockPath) {
    return null;
  }

  const prefix = nonDefaultLanguagePrefix(pathname, supportedLanguages);
  const bare = pathnameWithoutLanguagePrefix(blockPath, supportedLanguages);
  if (!prefix) return bare;
  if (bare === '/') return prefix;

  return `${prefix}${bare}`;
}

/**
 * Resolves a server redirect when a blocking hook is open and this request is elsewhere.
 * API routes stay available so the hook screen can still load its data.
 *
 * @param request - Incoming request.
 * @returns Hook pathname to redirect to, or null when this request should continue.
 */
export function getGeneralContentHookBlockRedirectPath(
  request: Request
): string | null {
  const pathname = new URL(request.url).pathname;
  if (isGeneralContentHookBlockRedirectExcludedPath(pathname)) return null;

  const blockPath = readGeneralContentHookBlockPath(request);
  if (!blockPath) return null;

  return resolveGeneralContentHookBlockPath({
    pathname,
    blockPath,
    supportedLanguages: getSupportedLanguages(),
  });
}

/**
 * Whether this document is the blocking hook screen and should render without the nav bar and footer.
 *
 * @param request - Incoming request.
 * @returns True when the block cookie matches the request pathname, including its language prefix.
 */
export function isGeneralContentHookNavigationHidden(
  request: Request
): boolean {
  const pathname = new URL(request.url).pathname;
  if (isGeneralContentHookBlockRedirectExcludedPath(pathname)) return false;

  const blockPath = readGeneralContentHookBlockPath(request);
  if (!blockPath) return false;

  return (
    resolveGeneralContentHookBlockPath({
      pathname,
      blockPath,
      supportedLanguages: getSupportedLanguages(),
    }) === null
  );
}
